import { useQueryFilter } from '../../hooks/useQueryFilter.js';
import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import type {
  AccountCreditTransaction,
  MembershipTier,
  TenantUser,
  UserRole,
} from '@atomfit/shared';
import { InfiniteScroll } from '../../components/common/InfiniteScroll.js';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag, formatCurrency } from '../../components/design/AtomFitUI.js';
import { usersApi } from '../../api/users.api.js';
import {
  adminApi,
  type FranchiseTenant,
  type PlatformMembershipPlan,
} from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';

type CustomerMembershipSummary = NonNullable<TenantUser['customerMemberships']>[number];

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; user: TenantUser }
  | { type: 'remove'; user: TenantUser }
  | { type: 'delete'; user: TenantUser }
  | { type: 'credit'; user: TenantUser }
  | { type: 'membership'; user: TenantUser }
  | {
      type: 'cancelMembership';
      user: TenantUser;
      membership: CustomerMembershipSummary;
    };

interface UserForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  tenantSlug: string;
  role: UserRole;
  isActive: boolean;
}

interface ManualMembershipForm {
  tenantId: string;
  tier: MembershipTier;
  months: string;
  creditAmount: string;
  slotPrice: string;
}

type CreditAdjustmentType = 'ADD' | 'REMOVE';

type UserRoleFilter = UserRole | 'CUSTOMER';
type UserColumnId =
  | 'user'
  | 'role'
  | 'phone'
  | 'bookings'
  | 'membership'
  | 'spending'
  | 'credit'
  | 'bookingDates'
  | 'status'
  | 'actions';

interface UserColumnDefinition {
  id: UserColumnId;
  label: string;
}

const allUserColumnIds: UserColumnId[] = [
  'user',
  'role',
  'phone',
  'bookings',
  'membership',
  'spending',
  'credit',
  'bookingDates',
  'status',
  'actions',
];

const NO_TENANT_VALUE = '__no_tenant__';
const localImmediateMembershipCancellation = import.meta.env.DEV;

const roleMeta: Record<UserRoleFilter, { label: string; color: string; bg: string }> = {
  PLATFORM_ADMIN: {
    label: translate('pages.admin.admin_users_page.role_meta.admin'),
    color: 'var(--af-accent)',
    bg: 'var(--af-accent-soft)',
  },
  OWNER_ADMIN: {
    label: translate('pages.admin.admin_users_page.role_meta.admin_2'),
    color: 'var(--af-success)',
    bg: 'rgba(74,222,128,0.1)',
  },
  GYM_STAFF: {
    label: translate('pages.admin.admin_users_page.role_meta.label'),
    color: 'var(--af-muted)',
    bg: 'rgba(255,255,255,0.06)',
  },
  CUSTOMER: {
    label: translate('pages.admin.admin_users_page.role_meta.user'),
    color: 'var(--af-dim)',
    bg: 'rgba(255,255,255,0.04)',
  },
};

function getUserColumns(isPlatform: boolean, isOperator: boolean): UserColumnDefinition[] {
  return [
    { id: 'user', label: translate('pages.admin.admin_users_page.get_user_columns.user') },
    { id: 'role', label: translate('pages.admin.admin_users_page.get_user_columns.role') },
    { id: 'phone', label: translate('pages.admin.admin_users_page.get_user_columns.phone') },
    { id: 'bookings', label: translate('pages.admin.admin_users_page.get_user_columns.booking') },
    ...(isPlatform
      ? [
          {
            id: 'membership' as const,
            label: translate('pages.admin.admin_users_page.get_user_columns.membership'),
          },
        ]
      : []),
    ...(!isOperator
      ? [
          {
            id: 'spending' as const,
            label: translate('pages.admin.admin_users_page.get_user_columns.label'),
          },
          {
            id: 'credit' as const,
            label: translate('pages.admin.admin_users_page.get_user_columns.credit_edit'),
          },
        ]
      : []),
    {
      id: 'bookingDates',
      label: translate('pages.admin.admin_users_page.get_user_columns.label_2'),
    },
    { id: 'status', label: translate('pages.admin.admin_users_page.get_user_columns.status') },
    { id: 'actions', label: translate('pages.admin.admin_users_page.get_user_columns.label_3') },
  ];
}

function loadVisibleColumns(storageKey: string, defaults: UserColumnId[]): Set<UserColumnId> {
  if (typeof window === 'undefined') return new Set(defaults);
  try {
    const saved = window.localStorage.getItem(storageKey);
    if (!saved) return new Set(defaults);
    const parsed: unknown = JSON.parse(saved);
    if (!Array.isArray(parsed)) return new Set(defaults);
    const validColumns = parsed.filter(
      (column): column is UserColumnId =>
        typeof column === 'string' && allUserColumnIds.includes(column as UserColumnId),
    );
    return new Set(validColumns.length > 0 ? validColumns : defaults);
  } catch {
    return new Set(defaults);
  }
}

const emptyForm: UserForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  tenantSlug: '',
  role: 'GYM_STAFF',
  isActive: true,
};

const emptyManualMembershipForm: ManualMembershipForm = {
  tenantId: '',
  tier: 'BASIC',
  months: '1',
  creditAmount: '',
  slotPrice: '',
};

const creditTypeLabels: Record<AccountCreditTransaction['type'], string> = {
  REFUND_CANCELLATION: translate(
    'pages.admin.admin_users_page.credit_type_labels.refund_cancellation',
  ),
  MANUAL_COMPENSATION: translate(
    'pages.admin.admin_users_page.credit_type_labels.manual_compensation',
  ),
  REFERRAL_BONUS: translate('pages.admin.admin_users_page.credit_type_labels.referral'),
  MEMBERSHIP_TOP_UP: translate('pages.admin.admin_users_page.credit_type_labels.membership_top_up'),
  MEMBERSHIP_EXPIRY: translate('pages.admin.admin_users_page.credit_type_labels.membership_expiry'),
  BOOKING_USAGE: translate('pages.admin.admin_users_page.credit_type_labels.booking_usage'),
};

function userToForm(user: TenantUser): UserForm {
  return {
    email: user.account.email,
    firstName: user.account.firstName ?? '',
    lastName: user.account.lastName ?? '',
    phone: user.account.phone ?? '',
    tenantSlug: user.tenant?.slug ?? '',
    role: user.role === 'CUSTOMER' ? 'GYM_STAFF' : user.role,
    isActive: user.account.isActive,
  };
}

function tenantAssignableRoles(tenantType?: FranchiseTenant['type']): UserRole[] {
  return tenantType === 'PLATFORM' ? ['PLATFORM_ADMIN'] : ['OWNER_ADMIN', 'GYM_STAFF'];
}

function formatDate(value: string | Date) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatOptionalDate(value?: string | Date | null) {
  return value ? formatDate(value) : '-';
}

function getRoleDisplay(user: TenantUser) {
  if (user.role !== 'CUSTOMER') return roleMeta[user.role];
  if (user.account.authMethod === 'PASSWORD') {
    return {
      label: translate('pages.admin.admin_users_page.get_role_display.registration'),
      color: 'var(--af-success)',
      bg: 'rgba(74,222,128,0.1)',
    };
  }
  return {
    label: translate('pages.admin.admin_users_page.get_role_display.guest'),
    color: 'var(--af-dim)',
    bg: 'rgba(255,255,255,0.04)',
  };
}

export function AdminUsersPage() {
  const navigate = useNavigate();
  const { role, account, tenantSlug, availableTenants } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const isOperator = role === 'GYM_STAFF';
  const userColumns = useMemo(
    () => getUserColumns(isPlatform, isOperator),
    [isOperator, isPlatform],
  );
  const columnStorageKey = `atomfit.admin.users.columns.${
    isPlatform ? 'platform' : isOperator ? 'operator' : 'owner'
  }`;

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [urlParams, setUrlParams] = useSearchParams();
  const [total, setTotal] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [query, setQuery] = useQueryFilter('q');
  const [queryInput, setQueryInput] = useState(urlParams.get('q') ?? '');
  const [roleFilter, setRoleFilter] = useQueryFilter<UserRoleFilter | ''>('role');
  useEffect(() => setQueryInput(query), [query]);
  const registeredFrom = urlParams.get('registeredFrom') ?? '';
  const registeredTo = urlParams.get('registeredTo') ?? '';
  const tenantSlugFilter = urlParams.get('tenantSlug') ?? '';
  const registrationFilters = useMemo(
    () => ({
      ...(registeredFrom ? { registeredFrom } : {}),
      ...(registeredTo ? { registeredTo } : {}),
      ...(tenantSlugFilter ? { tenantSlug: tenantSlugFilter } : {}),
    }),
    [registeredFrom, registeredTo, tenantSlugFilter],
  );
  const [visibleColumns, setVisibleColumns] = useState<Set<UserColumnId>>(() =>
    loadVisibleColumns(
      columnStorageKey,
      userColumns.map((column) => column.id),
    ),
  );
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [creditTransactions, setCreditTransactions] = useState<AccountCreditTransaction[]>([]);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditLoadingMore, setCreditLoadingMore] = useState(false);
  const [creditPage, setCreditPage] = useState(1);
  const [creditTotalPages, setCreditTotalPages] = useState(1);
  const [creditForm, setCreditForm] = useState<{
    adjustmentType: CreditAdjustmentType;
    amount: string;
    reason: string;
  }>({ adjustmentType: 'ADD', amount: '', reason: '' });
  const [membershipForm, setMembershipForm] =
    useState<ManualMembershipForm>(emptyManualMembershipForm);
  const [membershipPlans, setMembershipPlans] = useState<PlatformMembershipPlan[]>([]);
  const [membershipTenants, setMembershipTenants] = useState<
    { id: string; name: string; slug: string }[]
  >([]);
  const [createTenants, setCreateTenants] = useState<FranchiseTenant[]>([]);
  const [createTenantsLoading, setCreateTenantsLoading] = useState(isPlatform);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);
  const creditRequestIdRef = useRef(0);
  const currentTenantType =
    createTenants.find((tenant) => tenant.slug === tenantSlug)?.type ??
    availableTenants.find((item) => item.tenant.slug === tenantSlug)?.tenant.type;
  const allowedRoles = tenantAssignableRoles(currentTenantType);
  const selectedCreateTenant = createTenants.find((tenant) => tenant.slug === form.tenantSlug);
  const createAssignableRoles = tenantAssignableRoles(
    isPlatform ? selectedCreateTenant?.type : currentTenantType,
  );

  const userFilterParams = useMemo(
    () => ({
      ...registrationFilters,
      ...(query ? { q: query } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
    }),
    [query, roleFilter, registrationFilters],
  );

  const ownerUserFilterParams = useMemo(() => {
    const ownerRoleFilter = isOperator ? 'CUSTOMER' : roleFilter;
    return {
      ...registrationFilters,
      ...(query ? { q: query } : {}),
      ...(ownerRoleFilter ? { role: ownerRoleFilter } : {}),
    };
  }, [isOperator, query, roleFilter, registrationFilters]);

  const loadPage = useCallback(
    async (nextPage = 1, mode: 'replace' | 'append' = 'replace') => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (mode === 'replace') {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const res = isPlatform
          ? await usersApi.adminList({ ...userFilterParams, page: nextPage })
          : await usersApi.list({ ...ownerUserFilterParams, page: nextPage });
        if (requestId !== requestIdRef.current) return;
        setUsers((current) => {
          if (mode === 'replace') return res.data.data;
          const existing = new Set(current.map((user) => user.membershipId));
          return [...current, ...res.data.data.filter((user) => !existing.has(user.membershipId))];
        });
        setTotal(res.data.meta.total);
        setTotalCustomers(res.data.meta.totalCustomers ?? 0);
        setPage(res.data.meta.page);
        setTotalPages(res.data.meta.totalPages);
      } finally {
        if (mode === 'replace') {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [isPlatform, ownerUserFilterParams, userFilterParams],
  );

  useEffect(() => {
    void loadPage(1, 'replace');
  }, [loadPage]);

  useEffect(() => {
    if (!isPlatform) {
      setCreateTenants([]);
      setCreateTenantsLoading(false);
      return;
    }

    let cancelled = false;
    async function loadCreateTenants() {
      setCreateTenantsLoading(true);
      try {
        const firstPage = await adminApi.listTenants(1, 100);
        const remainingPages = Array.from(
          { length: Math.max(0, firstPage.data.meta.totalPages - 1) },
          (_, index) => index + 2,
        );
        const remaining = await Promise.all(
          remainingPages.map((page) => adminApi.listTenants(page, 100)),
        );
        if (cancelled) return;
        setCreateTenants(
          [firstPage, ...remaining]
            .flatMap((response) => response.data.data)
            .filter((tenant) => tenant.isActive),
        );
      } catch {
        if (!cancelled) setCreateTenants([]);
      } finally {
        if (!cancelled) setCreateTenantsLoading(false);
      }
    }

    void loadCreateTenants();
    return () => {
      cancelled = true;
    };
  }, [isPlatform]);

  useEffect(() => {
    try {
      window.localStorage.setItem(columnStorageKey, JSON.stringify([...visibleColumns]));
    } catch {
      // Column preferences are optional when browser storage is unavailable.
    }
  }, [columnStorageKey, visibleColumns]);

  function toggleColumn(columnId: UserColumnId) {
    setVisibleColumns((current) => {
      if (current.size === 1 && current.has(columnId)) return current;
      const next = new Set(current);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  }

  function openCreate() {
    const currentTenant = createTenants.find((tenant) => tenant.slug === tenantSlug);
    const targetTenant = currentTenant?.type === 'OWNER' ? currentTenant : undefined;
    setForm({
      ...emptyForm,
      tenantSlug: isPlatform ? (targetTenant?.slug ?? NO_TENANT_VALUE) : (tenantSlug ?? ''),
      role: 'GYM_STAFF',
    });
    setError(null);
    setModal({ type: 'create' });
    setTimeout(() => emailRef.current?.focus(), 50);
  }

  function openEdit(user: TenantUser) {
    setForm(userToForm(user));
    setError(null);
    setModal({ type: 'edit', user });
  }

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setQuery(queryInput.trim());
  }

  async function saveUser(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    if (form.phone.trim() && !isValidPhoneNumber(form.phone)) {
      setError(translate('pages.admin.admin_users_page.save_user.set_error.phone'));
      setSaving(false);
      return;
    }
    if (modal.type === 'create' && isPlatform && !form.tenantSlug) {
      setError(translate('pages.admin.admin_users_page.save_user.set_error.user_tenant_add'));
      setSaving(false);
      return;
    }
    try {
      if (modal.type === 'create') {
        const accountData = {
          email: form.email.trim(),
          firstName: form.firstName || undefined,
          lastName: form.lastName || undefined,
          phone: form.phone || undefined,
        };
        if (isPlatform && form.tenantSlug === NO_TENANT_VALUE) {
          await adminApi.createCustomer(accountData);
        } else {
          await usersApi.create(
            { ...accountData, role: form.role },
            isPlatform ? form.tenantSlug : undefined,
          );
        }
      } else if (modal.type === 'edit') {
        if (modal.user.role === 'CUSTOMER') {
          await adminApi.updateCustomer(modal.user.account.id, {
            email: form.email.trim(),
            firstName: form.firstName || null,
            lastName: form.lastName || null,
            phone: form.phone || null,
            isActive: form.isActive,
          });
        } else {
          await usersApi.update(
            modal.user.membershipId,
            {
              firstName: form.firstName || null,
              lastName: form.lastName || null,
              phone: form.phone || null,
              role: form.role,
            },
            isPlatform ? modal.user.tenant?.slug : undefined,
          );
        }
      }
      setModal({ type: 'none' });
      void loadPage(1, 'replace');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ?? translate('pages.admin.admin_users_page.save_user.set_error.error_user_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function toggleCustomerStatus(user: TenantUser) {
    if (!isPlatform || user.role !== 'CUSTOMER') return;
    setSaving(true);
    setError(null);
    try {
      await adminApi.updateCustomer(user.account.id, { isActive: !user.account.isActive });
      void loadPage(1, 'replace');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ??
          translate('pages.admin.admin_users_page.toggle_customer_status.set_error.error_user'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeUser() {
    if (modal.type !== 'remove') return;
    setSaving(true);
    setError(null);
    try {
      await usersApi.remove(
        modal.user.membershipId,
        isPlatform ? modal.user.tenant?.slug : undefined,
      );
      setModal({ type: 'none' });
      void loadPage(1, 'replace');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? translate('pages.admin.admin_users_page.remove_user.set_error.error_user'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteCustomer() {
    if (
      modal.type !== 'delete' ||
      deleteConfirmation.trim().toLowerCase() !== modal.user.account.email.toLowerCase()
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await adminApi.deleteCustomer(modal.user.account.id);
      setModal({ type: 'none' });
      setDeleteConfirmation('');
      void loadPage(1, 'replace');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ??
          translate('pages.admin.admin_users_page.delete_customer.set_error.error_user_delete'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function loadCreditTransactions(
    accountId: string,
    nextPage = 1,
    mode: 'replace' | 'append' = 'replace',
  ) {
    if (!isPlatform) return;
    const requestId =
      mode === 'replace' ? creditRequestIdRef.current + 1 : creditRequestIdRef.current;
    if (mode === 'replace') {
      creditRequestIdRef.current = requestId;
      setCreditLoading(true);
      setCreditLoadingMore(false);
    } else {
      setCreditLoadingMore(true);
    }
    try {
      const res = await adminApi.listCredits({ page: nextPage, accountId });
      if (requestId !== creditRequestIdRef.current) return;
      setCreditTransactions((current) => {
        if (mode === 'replace') return res.data.data;
        const existingIds = new Set(current.map((transaction) => transaction.id));
        return [
          ...current,
          ...res.data.data.filter((transaction) => !existingIds.has(transaction.id)),
        ];
      });
      setCreditPage(res.data.meta.page);
      setCreditTotalPages(res.data.meta.totalPages);
    } finally {
      if (requestId === creditRequestIdRef.current) {
        if (mode === 'replace') setCreditLoading(false);
        else setCreditLoadingMore(false);
      }
    }
  }

  function openCredit(user: TenantUser) {
    setError(null);
    setCreditForm({ adjustmentType: 'ADD', amount: '', reason: '' });
    setCreditTransactions([]);
    setCreditPage(1);
    setCreditTotalPages(1);
    setModal({ type: 'credit', user });
    void loadCreditTransactions(user.account.id);
  }

  async function openMembership(user: TenantUser) {
    setError(null);
    setMembershipForm(emptyManualMembershipForm);
    setModal({ type: 'membership', user });
    setMembershipLoading(true);
    try {
      const [plansResponse, tenantsResponse] = await Promise.all([
        adminApi.listMembershipPlans(),
        adminApi.listTenants(1, 100),
      ]);
      const activePlans = plansResponse.data.data.filter((plan) => plan.isActive);
      const activeTenants = tenantsResponse.data.data
        .filter((tenant) => tenant.isActive)
        .map((tenant) => ({ id: tenant.id, name: tenant.name, slug: tenant.slug }));
      setMembershipPlans(activePlans);
      setMembershipTenants(activeTenants);
      setMembershipForm((current) => ({
        ...current,
        ...(activePlans[0] ? { tier: activePlans[0].tier } : {}),
        ...(activeTenants[0] ? { tenantId: activeTenants[0].id } : {}),
      }));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ?? translate('pages.admin.admin_users_page.open_membership.set_error.error_membership'),
      );
      setMembershipPlans([]);
      setMembershipTenants([]);
    } finally {
      setMembershipLoading(false);
    }
  }

  async function grantManualMembership(event: React.FormEvent) {
    event.preventDefault();
    if (modal.type !== 'membership') return;

    const months = Number(membershipForm.months);
    if (!Number.isInteger(months) || months < 1 || months > 60) {
      setError(translate('pages.admin.admin_users_page.grant_manual_membership.set_error.time'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await adminApi.grantManualMembership(modal.user.account.id, {
        tenantId: membershipForm.tenantId,
        tier: membershipForm.tier,
        months,
        ...(membershipForm.creditAmount.trim() ? { creditAmountInSmallestUnit: Math.round(Number(membershipForm.creditAmount) * 100) } : {}),
        ...(membershipForm.slotPrice.trim() ? { slotPriceInSmallestUnit: Math.round(Number(membershipForm.slotPrice) * 100) } : {}),
      });
      setModal({ type: 'none' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ??
          translate(
            'pages.admin.admin_users_page.grant_manual_membership.set_error.error_membership_active',
          ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function cancelMembership() {
    if (modal.type !== 'cancelMembership') return;
    setSaving(true);
    setError(null);
    try {
      await adminApi.cancelMembership(modal.user.account.id, modal.membership.id);
      setModal({ type: 'none' });
      await loadPage(1, 'replace');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ??
          translate(
            'pages.admin.admin_users_page.cancel_membership.set_error.error_membership_cancel',
          ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function grantCredit(event: React.FormEvent) {
    event.preventDefault();
    if (modal.type !== 'credit') return;
    setSaving(true);
    setError(null);
    try {
      const amountInSmallestUnit = Math.round(Number(creditForm.amount.replace(',', '.')) * 100);
      const response = await adminApi.createManualCredit({
        accountId: modal.user.account.id,
        amountInSmallestUnit:
          creditForm.adjustmentType === 'REMOVE' ? -amountInSmallestUnit : amountInSmallestUnit,
        currency: 'CZK',
        reason: creditForm.reason.trim(),
      });
      setUsers((current) =>
        current.map((user) =>
          user.account.id === modal.user.account.id
            ? {
                ...user,
                creditBalanceInSmallestUnit:
                  user.creditBalanceInSmallestUnit + response.data.amountInSmallestUnit,
              }
            : user,
        ),
      );
      setCreditForm((current) => ({ ...current, amount: '', reason: '' }));
      await loadCreditTransactions(modal.user.account.id);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        creditForm.adjustmentType === 'REMOVE' && msg === 'Insufficient account credit'
          ? translate(
              'pages.admin.admin_users_page.grant_credit.set_error.insufficient_credit_remove',
            )
          : (msg ??
              translate(
                creditForm.adjustmentType === 'ADD'
                  ? 'pages.admin.admin_users_page.grant_credit.set_error.error_credit_add'
                  : 'pages.admin.admin_users_page.grant_credit.set_error.error_credit_remove',
              )),
      );
    } finally {
      setSaving(false);
    }
  }

  async function exportCustomers() {
    setExporting(true);
    setExportError(false);
    try {
      const response = await adminApi.exportCustomers({
        ...registrationFilters,
        ...(query ? { q: query } : {}),
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'customers.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  const showForm = modal.type === 'create' || modal.type === 'edit';

  return (
    <AdminLayout
      title={
        isOperator
          ? translate('pages.admin.admin_users_page.admin_users_page.admin_layout.title')
          : translate('pages.admin.admin_users_page.admin_users_page.admin_layout.user')
      }
      eyebrow={
        isPlatform
          ? translate('pages.admin.admin_users_page.admin_users_page.admin_layout.eyebrow')
          : isOperator
            ? translate('pages.admin.admin_users_page.admin_users_page.admin_layout.eyebrow_2')
            : translate('pages.admin.admin_users_page.admin_users_page.admin_layout.eyebrow_3')
      }
      headerActions={
        isPlatform && (
          <button
            className="af-primary-btn"
            style={{ padding: '12px 18px', fontSize: 13 }}
            type="button"
            onClick={() => void exportCustomers()}
            disabled={exporting || Boolean(roleFilter && roleFilter !== 'CUSTOMER')}
          >
            {translate(
              exporting ? 'admin.customers.export.loading' : 'admin.customers.export.button',
            )}
          </button>
        )
      }
    >
      <p aria-live="polite">
        {loading
          ? translate('report.loading')
          : translate('report.customerCount', { total, customers: totalCustomers })}
      </p>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'end' }}>
        {(['registeredFrom', 'registeredTo'] as const).map((key) => (
          <label className="af-label" key={key}>
            {translate(key === 'registeredFrom' ? 'report.registeredFrom' : 'report.registeredTo')}
            <input
              className="af-input"
              type="date"
              value={urlParams.get(key) ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setUrlParams(
                  (current) => {
                    const next = new URLSearchParams(current);
                    if (value) next.set(key, value);
                    else next.delete(key);
                    return next;
                  },
                  { replace: true },
                );
              }}
            />
          </label>
        ))}
        {tenantSlugFilter && (
          <button
            className="af-outline-btn"
            onClick={() =>
              setUrlParams((current) => {
                const next = new URLSearchParams(current);
                next.delete('tenantSlug');
                return next;
              })
            }
          >
            {translate('report.clearScope')} ({tenantSlugFilter})
          </button>
        )}
      </div>
      {isPlatform && exportError && (
        <p role="alert">{translate('pages.admin.admin_users_page.admin_users_page.p.error')}</p>
      )}

      <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
        <form
          onSubmit={applySearch}
          style={{
            display: 'grid',
            gridTemplateColumns: `minmax(220px, 1fr) 210px auto auto${isOperator ? '' : ' auto'}`,
            gap: 12,
            alignItems: 'end',
          }}
        >
          <label className="af-label">
            {translate('pages.admin.admin_users_page.admin_users_page.label_label.search')}

            <input
              className="af-input"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={translate(
                'pages.admin.admin_users_page.admin_users_page.input_input.placeholder_email_name',
              )}
            />
          </label>
          <label className="af-label">
            {translate('pages.admin.admin_users_page.admin_users_page.label_label.role')}

            <CustomSelect
              value={roleFilter}
              onChange={(value) => {
                setRoleFilter(value as UserRoleFilter | '');
              }}
              options={[
                {
                  value: '',
                  label: translate(
                    'pages.admin.admin_users_page.admin_users_page.custom_select.options',
                  ),
                },
                ...(isOperator
                  ? (['CUSTOMER'] as UserRoleFilter[])
                  : isPlatform
                    ? ([
                        'PLATFORM_ADMIN',
                        'OWNER_ADMIN',
                        'GYM_STAFF',
                        'CUSTOMER',
                      ] as UserRoleFilter[])
                    : (['OWNER_ADMIN', 'GYM_STAFF', 'CUSTOMER'] as UserRoleFilter[])
                ).map((item) => ({ value: item, label: roleMeta[item].label })),
              ]}
            />
          </label>
          <button className="af-outline-btn" style={{ padding: '12px 16px' }} type="submit">
            {translate('pages.admin.admin_users_page.admin_users_page.button_outline_btn.filter')}
          </button>
          <ColumnVisibilityMenu
            columns={userColumns}
            visibleColumns={visibleColumns}
            onToggle={toggleColumn}
            onShowAll={() => setVisibleColumns(new Set(userColumns.map((column) => column.id)))}
          />

          {!isOperator && (
            <button
              className="af-primary-btn"
              style={{ padding: '12px 18px', fontSize: 13 }}
              type="button"
              onClick={openCreate}
              disabled={createTenantsLoading}
            >
              {translate(
                'pages.admin.admin_users_page.admin_users_page.button_primary_btn.user_add',
              )}
            </button>
          )}
        </form>
      </div>

      <div className="af-card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 24,
              color: 'var(--af-muted)',
            }}
          >
            <Spinner />{' '}
            {translate('pages.admin.admin_users_page.admin_users_page.div.loading_user')}
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
            {translate('pages.admin.admin_users_page.admin_users_page.div.user')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                {userColumns
                  .filter((column) => visibleColumns.has(column.id))
                  .map((column) => (
                    <th
                      key={column.id}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: 'var(--af-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {column.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const fullName = [user.account.firstName, user.account.lastName]
                  .filter(Boolean)
                  .join(' ');
                const currentUser = user.account.id === account?.id;
                const membershipBacked = user.role !== 'CUSTOMER' && Boolean(user.tenant?.slug);
                const roleDisplay = getRoleDisplay(user);
                return (
                  <tr
                    key={user.membershipId}
                    style={{
                      borderBottom:
                        index < users.length - 1 ? '1px solid var(--af-border)' : 'none',
                    }}
                  >
                    {visibleColumns.has('user') && (
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800 }}>{fullName || user.account.email}</div>
                        <div
                          className="af-mono"
                          style={{ fontSize: 11, color: 'var(--af-dim)', marginTop: 2 }}
                        >
                          {user.account.email}
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('role') && (
                      <td style={{ padding: '14px 16px' }}>
                        <Tag color={roleDisplay.color} bg={roleDisplay.bg}>
                          {roleDisplay.label}
                        </Tag>
                        {isPlatform && user.tenant && (
                          <div
                            className="af-mono"
                            style={{ fontSize: 10, color: 'var(--af-dim)', marginTop: 5 }}
                          >
                            {user.tenant.name} {'·'} {user.tenant.slug}
                          </div>
                        )}
                      </td>
                    )}
                    {visibleColumns.has('phone') && (
                      <td
                        className="af-mono"
                        style={{
                          padding: '14px 16px',
                          color: 'var(--af-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.account.phone ?? '-'}
                      </td>
                    )}
                    {visibleColumns.has('bookings') && (
                      <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                        {user.bookingsCount}
                      </td>
                    )}
                    {isPlatform && visibleColumns.has('membership') && (
                      <td style={{ padding: '10px 16px', minWidth: 180 }}>
                        {user.customerMemberships?.length ? (
                          <div style={{ display: 'grid', gap: 8 }}>
                            {user.customerMemberships.map((membership) => {
                              const statusMeta = {
                                ACTIVE: {
                                  label: membership.cancelAtPeriodEnd
                                    ? translate(
                                        'pages.admin.admin_users_page.admin_users_page.div.copy',
                                      )
                                    : translate(
                                        'pages.admin.admin_users_page.admin_users_page.div.active',
                                      ),
                                  color: membership.cancelAtPeriodEnd
                                    ? 'var(--af-peak)'
                                    : 'var(--af-success)',
                                  bg: membership.cancelAtPeriodEnd
                                    ? 'var(--af-peak-soft)'
                                    : 'rgba(74,222,128,0.1)',
                                },
                                INACTIVE: {
                                  label: translate(
                                    'pages.admin.admin_users_page.admin_users_page.div.inactive',
                                  ),
                                  color: 'var(--af-dim)',
                                  bg: 'rgba(255,255,255,0.04)',
                                },
                                CANCELLED: {
                                  label: translate(
                                    'pages.admin.admin_users_page.admin_users_page.div.cancel',
                                  ),
                                  color: 'var(--af-danger)',
                                  bg: 'rgba(232,66,26,0.1)',
                                },
                              }[membership.status];
                              const endsAt = membership.expiresAt ?? membership.currentPeriodEnd;
                              return (
                                <div key={membership.id}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <strong>{membership.plan.name || membership.tier}</strong>
                                    <Tag color={statusMeta.color} bg={statusMeta.bg}>
                                      {statusMeta.label}
                                    </Tag>
                                  </div>
                                  <div
                                    className="af-mono"
                                    style={{ fontSize: 10, color: 'var(--af-dim)', marginTop: 3 }}
                                  >
                                    {membership.tenant.name}
                                    {endsAt
                                      ? translate(
                                          'pages.admin.admin_users_page.admin_users_page.div_mono.copy',
                                          { value1: formatDate(endsAt) },
                                        )
                                      : ''}
                                  </div>
                                  {membership.status === 'ACTIVE' &&
                                    !membership.cancelAtPeriodEnd && (
                                      <button
                                        className="af-outline-btn"
                                        type="button"
                                        style={{
                                          marginTop: 6,
                                          padding: '5px 8px',
                                          color: 'var(--af-danger)',
                                          fontSize: 10,
                                        }}
                                        disabled={saving}
                                        onClick={() => {
                                          setError(null);
                                          setModal({
                                            type: 'cancelMembership',
                                            user,
                                            membership,
                                          });
                                        }}
                                      >
                                        {translate(
                                          'pages.admin.admin_users_page.admin_users_page.button_outline_btn.membership_cancel',
                                        )}
                                      </button>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--af-dim)' }}>{'-'}</span>
                        )}
                      </td>
                    )}
                    {!isOperator && visibleColumns.has('spending') && (
                      <td
                        style={{
                          padding: '14px 16px',
                          color: 'var(--af-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatCurrency(user.totalSpendingInSmallestUnit, user.currency)}
                      </td>
                    )}
                    {!isOperator && visibleColumns.has('credit') && (
                      <td
                        style={{
                          padding: '14px 16px',
                          color: 'var(--af-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatCurrency(user.creditBalanceInSmallestUnit, user.currency)}
                      </td>
                    )}
                    {visibleColumns.has('bookingDates') && (
                      <td
                        className="af-mono"
                        style={{
                          padding: '14px 16px',
                          color: 'var(--af-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatOptionalDate(user.firstBookingAt)} {'/'}{' '}
                        {formatOptionalDate(user.lastBookingAt)}
                      </td>
                    )}
                    {visibleColumns.has('status') && (
                      <td style={{ padding: '14px 16px' }}>
                        {user.account.isActive ? (
                          <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
                            {translate('pages.admin.admin_users_page.admin_users_page.tag.active')}
                          </Tag>
                        ) : (
                          <Tag color="var(--af-danger)" bg="rgba(232,66,26,0.1)">
                            {translate('pages.admin.admin_users_page.admin_users_page.tag.copy')}
                          </Tag>
                        )}
                      </td>
                    )}
                    {visibleColumns.has('actions') && (
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <UserActionsDropdown
                          disabled={saving}
                          actions={[
                            {
                              label: translate(
                                'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions',
                              ),
                              onSelect: () => {
                                const params = new URLSearchParams({
                                  accountId: user.account.id,
                                  q: user.account.email,
                                  range: 'all',
                                });
                                navigate(`/admin/bookings?${params.toString()}`);
                              },
                            },
                            ...(!isOperator
                              ? [
                                  {
                                    label: translate(
                                      'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_2',
                                    ),
                                    disabled:
                                      !membershipBacked &&
                                      !(isPlatform && user.role === 'CUSTOMER'),
                                    onSelect: () => openEdit(user),
                                  },
                                  ...(membershipBacked
                                    ? [
                                        {
                                          label: translate(
                                            'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_3',
                                          ),
                                          tone: 'danger' as const,
                                          disabled: currentUser,
                                          onSelect: () =>
                                            setModal({ type: 'remove' as const, user }),
                                        },
                                      ]
                                    : []),
                                ]
                              : []),
                            ...(isPlatform
                              ? [
                                  {
                                    label: translate(
                                      'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_4',
                                    ),
                                    onSelect: () => openCredit(user),
                                  },
                                ]
                              : []),
                            ...(isPlatform && user.role === 'CUSTOMER'
                              ? [
                                  {
                                    label: translate(
                                      'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_5',
                                    ),
                                    onSelect: () => void openMembership(user),
                                  },
                                  {
                                    label: user.account.isActive
                                      ? translate(
                                          'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_6',
                                        )
                                      : translate(
                                          'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_7',
                                        ),
                                    tone: user.account.isActive
                                      ? ('danger' as const)
                                      : ('success' as const),
                                    onSelect: () => void toggleCustomerStatus(user),
                                  },
                                  {
                                    label: translate(
                                      'pages.admin.admin_users_page.admin_users_page.user_actions_dropdown.actions_8',
                                    ),
                                    tone: 'danger' as const,
                                    onSelect: () => {
                                      setDeleteConfirmation('');
                                      setError(null);
                                      setModal({ type: 'delete', user });
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && users.length > 0 && (
        <InfiniteScroll
          hasMore={page < totalPages}
          isLoading={loadingMore}
          onLoadMore={() => void loadPage(page + 1, 'append')}
          loadingLabel={translate(
            'pages.admin.admin_users_page.admin_users_page.infinite_scroll.loading_label',
          )}
          endLabel={translate(
            'pages.admin.admin_users_page.admin_users_page.infinite_scroll.end_label',
          )}
          style={{ marginTop: 8 }}
        />
      )}

      {showForm && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <form
            className="af-card"
            style={{ width: '100%', maxWidth: 520, padding: 28 }}
            onSubmit={(event) => void saveUser(event)}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800 }}>
              {modal.type === 'create'
                ? translate('pages.admin.admin_users_page.admin_users_page.h2.user_add')
                : translate('pages.admin.admin_users_page.admin_users_page.h2.user_edit')}
            </h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <label className="af-label">
                {translate('pages.admin.admin_users_page.admin_users_page.label_label.email')}

                <input
                  ref={emailRef}
                  className="af-input"
                  type="email"
                  value={form.email}
                  disabled={modal.type === 'edit' && modal.user.role !== 'CUSTOMER'}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_users_page.admin_users_page.label_label.name')}

                  <input
                    className="af-input"
                    value={form.firstName}
                    onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_users_page.admin_users_page.label_label.last_name')}

                  <input
                    className="af-input"
                    value={form.lastName}
                    onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </label>
              </div>
              {modal.type === 'create' && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      isPlatform && form.tenantSlug !== NO_TENANT_VALUE ? '1fr 1fr' : '1fr',
                    gap: 14,
                  }}
                >
                  {isPlatform && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_users_page.admin_users_page.label_label.tenant',
                      )}

                      <CustomSelect
                        value={form.tenantSlug}
                        onChange={(nextTenantSlug) => {
                          const nextTenant = createTenants.find(
                            (tenant) => tenant.slug === nextTenantSlug,
                          );
                          const nextRoles = tenantAssignableRoles(nextTenant?.type);
                          setForm((current) => ({
                            ...current,
                            tenantSlug: nextTenantSlug,
                            role: nextRoles.includes(current.role)
                              ? current.role
                              : nextTenant?.type === 'PLATFORM'
                                ? 'PLATFORM_ADMIN'
                                : 'GYM_STAFF',
                          }));
                        }}
                        options={[
                          {
                            value: NO_TENANT_VALUE,
                            label: translate(
                              'pages.admin.admin_users_page.admin_users_page.custom_select.options_2',
                            ),
                          },
                          ...createTenants.map((tenant) => ({
                            value: tenant.slug,
                            label: translate(
                              'pages.admin.admin_users_page.admin_users_page.custom_select.options_3',
                              {
                                value1: tenant.name,
                                value2: tenant.slug,
                              },
                            ),
                          })),
                        ]}
                        placeholder={translate(
                          'pages.admin.admin_users_page.admin_users_page.custom_select.placeholder_tenant_select',
                        )}
                      />
                    </label>
                  )}
                  {(!isPlatform || form.tenantSlug !== NO_TENANT_VALUE) && (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_users_page.admin_users_page.label_label.role_4',
                      )}

                      <CustomSelect
                        value={form.role}
                        onChange={(nextRole) =>
                          setForm((current) => ({ ...current, role: nextRole as UserRole }))
                        }
                        options={createAssignableRoles.map((assignableRole) => ({
                          value: assignableRole,
                          label: roleMeta[assignableRole].label,
                        }))}
                      />
                    </label>
                  )}
                </div>
              )}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: modal.type === 'edit' ? '1fr 1fr' : '1fr',
                  gap: 14,
                }}
              >
                <label className="af-label">
                  {translate('pages.admin.admin_users_page.admin_users_page.label_label.phone')}

                  <PhoneInput
                    className="af-phone-input"
                    defaultCountry="CZ"
                    international
                    countryCallingCodeEditable
                    value={form.phone}
                    onChange={(value) => setForm((p) => ({ ...p, phone: value ?? '' }))}
                    placeholder={'+420 777 123 456'}
                  />
                </label>
                {modal.type === 'edit' &&
                  (modal.user.role === 'CUSTOMER' ? (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_users_page.admin_users_page.label_label.status',
                      )}

                      <CustomSelect
                        value={form.isActive ? 'active' : 'inactive'}
                        onChange={(value) =>
                          setForm((p) => ({ ...p, isActive: value === 'active' }))
                        }
                        options={[
                          {
                            value: 'active',
                            label: translate(
                              'pages.admin.admin_users_page.admin_users_page.custom_select.options_4',
                            ),
                          },
                          {
                            value: 'inactive',
                            label: translate(
                              'pages.admin.admin_users_page.admin_users_page.custom_select.options_5',
                            ),
                          },
                        ]}
                      />
                    </label>
                  ) : (
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_users_page.admin_users_page.label_label.role_5',
                      )}

                      <CustomSelect
                        value={form.role}
                        onChange={(role) => setForm((p) => ({ ...p, role: role as UserRole }))}
                        options={(modal.user.tenant?.type === 'OWNER'
                          ? (['OWNER_ADMIN', 'GYM_STAFF'] as UserRole[])
                          : allowedRoles
                        ).map((item) => ({ value: item, label: roleMeta[item].label }))}
                      />
                    </label>
                  ))}
              </div>
              {modal.type === 'create' && (
                <div
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--af-border)',
                    background: 'var(--af-s2)',
                    borderRadius: 8,
                    color: 'var(--af-muted)',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {isPlatform && form.tenantSlug === NO_TENANT_VALUE
                    ? translate(
                        'pages.admin.admin_users_page.admin_users_page.div.user_tenant_create',
                      )
                    : translate('pages.admin.admin_users_page.admin_users_page.div.user_2', {
                        value1: roleMeta[form.role].label,
                        value2: selectedCreateTenant
                          ? ` do tenantu ${selectedCreateTenant.name}`
                          : '',
                      })}{' '}
                  {translate(
                    'pages.admin.admin_users_page.admin_users_page.div.password_email_create',
                  )}
                </div>
              )}
              {error && <div className="af-error">{error}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_users_page.admin_users_page.button_outline_btn.cancel',
                )}
              </button>
              <button
                className="af-primary-btn"
                style={{ padding: '10px 22px', fontSize: 14 }}
                disabled={saving || (modal.type === 'create' && isPlatform && !form.tenantSlug)}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_users_page.admin_users_page.button_primary_btn.save')
                )}
              </button>
            </div>
          </form>
        </Overlay>
      )}

      {modal.type === 'remove' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 420, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_users_page.admin_users_page.h2.user')}
            </h2>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_users_page.admin_users_page.p.user')}{' '}
              <strong style={{ color: 'var(--af-text)' }}>{modal.user.account.email}</strong>
              {translate('pages.admin.admin_users_page.admin_users_page.p.booking_tenant')}
            </p>
            {error && (
              <div className="af-error" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate('pages.admin.admin_users_page.admin_users_page.button_outline_btn.back')}
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                }}
                disabled={saving}
                onClick={() => void removeUser()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_users_page.admin_users_page.button.label')
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {modal.type === 'delete' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 460, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_users_page.admin_users_page.h2.delete')}
            </h2>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_users_page.admin_users_page.p.description')}{' '}
              <strong style={{ color: 'var(--af-text)' }}>{modal.user.account.email}</strong>
              {translate(
                'pages.admin.admin_users_page.admin_users_page.p.booking_payment_membership',
              )}
            </p>
            <label className="af-label" style={{ marginBottom: 16 }}>
              {translate(
                'pages.admin.admin_users_page.admin_users_page.label_label.user_email_confirmation',
              )}

              <input
                className="af-input"
                type="email"
                autoComplete="off"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder={modal.user.account.email}
                disabled={saving}
              />
            </label>
            {error && (
              <div className="af-error" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_users_page.admin_users_page.button_outline_btn.back_2',
                )}
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                }}
                disabled={
                  saving ||
                  deleteConfirmation.trim().toLowerCase() !== modal.user.account.email.toLowerCase()
                }
                onClick={() => void deleteCustomer()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_users_page.admin_users_page.button.delete')
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {modal.type === 'cancelMembership' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 460, padding: 28 }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_users_page.admin_users_page.h2.membership_cancel')}
            </h2>
            <div
              className="af-mono"
              style={{ fontSize: 12, color: 'var(--af-muted)', marginBottom: 16 }}
            >
              {modal.user.account.email}
            </div>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate(
                localImmediateMembershipCancellation || !modal.membership.hasStripeSubscription
                  ? 'pages.admin.admin_users_page.admin_users_page.p.membership_cancel_immediate'
                  : 'pages.admin.admin_users_page.admin_users_page.p.membership_cancel_period_end',
                {
                  value1: modal.membership.plan.name || modal.membership.tier,
                  value2: modal.membership.tenant.name,
                  value3: modal.membership.currentPeriodEnd
                    ? formatDate(modal.membership.currentPeriodEnd)
                    : translate(
                        'pages.admin.admin_users_page.admin_users_page.p.membership_cancel_period_fallback',
                      ),
                },
              )}
            </p>
            {error && (
              <div className="af-error" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_users_page.admin_users_page.button_outline_btn.back_3',
                )}
              </button>
              <button
                type="button"
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                }}
                disabled={saving}
                onClick={() => void cancelMembership()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate(
                    'pages.admin.admin_users_page.admin_users_page.button_primary_btn.membership_cancel',
                  )
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {modal.type === 'credit' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <div
            className="af-card"
            style={{
              width: 'min(760px, 100%)',
              maxHeight: 'calc(100dvh - 48px)',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '28px 28px 0', flex: '0 0 auto' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800 }}>
                {translate('pages.admin.admin_users_page.admin_users_page.h2.user_credit_edit')}
              </h2>
              <div
                className="af-mono"
                style={{ fontSize: 12, color: 'var(--af-muted)', marginBottom: 18 }}
              >
                {modal.user.account.email}
              </div>
              <div
                style={{
                  color: 'var(--af-muted)',
                  fontSize: 13,
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                {translate('pages.admin.admin_users_page.admin_users_page.div.credit_tenant_edit')}
              </div>
              <form
                onSubmit={(event) => void grantCredit(event)}
                style={{ display: 'grid', gap: 14, marginBottom: 22 }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 12,
                    alignItems: 'end',
                  }}
                >
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_users_page.admin_users_page.label_label.credit_action',
                    )}

                    <CustomSelect
                      value={creditForm.adjustmentType}
                      onChange={(adjustmentType) =>
                        setCreditForm((current) => ({
                          ...current,
                          adjustmentType: adjustmentType as CreditAdjustmentType,
                        }))
                      }
                      options={[
                        {
                          value: 'ADD',
                          label: translate(
                            'pages.admin.admin_users_page.admin_users_page.custom_select.credit_add',
                          ),
                        },
                        {
                          value: 'REMOVE',
                          label: translate(
                            'pages.admin.admin_users_page.admin_users_page.custom_select.credit_remove',
                          ),
                        },
                      ]}
                      disabled={saving}
                    />
                  </label>
                  <label className="af-label">
                    {translate('pages.admin.admin_users_page.admin_users_page.label_label.price')}

                    <input
                      className="af-input"
                      inputMode="decimal"
                      min="1"
                      step="0.01"
                      value={creditForm.amount}
                      onChange={(e) => setCreditForm((p) => ({ ...p, amount: e.target.value }))}
                      disabled={saving}
                      required
                    />
                  </label>
                  <label className="af-label">
                    {translate('pages.admin.admin_users_page.admin_users_page.label_label.reason')}

                    <input
                      className="af-input"
                      value={creditForm.reason}
                      onChange={(e) => setCreditForm((p) => ({ ...p, reason: e.target.value }))}
                      maxLength={500}
                      disabled={saving}
                      required
                    />
                  </label>
                  <button
                    className={creditForm.adjustmentType === 'ADD' ? 'af-primary-btn' : undefined}
                    style={
                      creditForm.adjustmentType === 'ADD'
                        ? { padding: '12px 18px', fontSize: 13 }
                        : {
                            padding: '12px 18px',
                            borderRadius: 8,
                            border: '1px solid rgba(232,66,26,0.4)',
                            background: 'rgba(232,66,26,0.12)',
                            color: 'var(--af-danger)',
                            fontSize: 13,
                            fontWeight: 800,
                          }
                    }
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner />
                    ) : (
                      translate(
                        creditForm.adjustmentType === 'ADD'
                          ? 'pages.admin.admin_users_page.admin_users_page.button_primary_btn.credit_add_edit'
                          : 'pages.admin.admin_users_page.admin_users_page.button.credit_remove',
                      )
                    )}
                  </button>
                </div>
                {creditForm.adjustmentType === 'REMOVE' && (
                  <div style={{ color: 'var(--af-muted)', fontSize: 12, lineHeight: 1.5 }}>
                    {translate(
                      'pages.admin.admin_users_page.admin_users_page.div.credit_remove_limit',
                    )}
                  </div>
                )}
                {error && <div className="af-error">{error}</div>}
              </form>
            </div>
            <div
              style={{
                flex: '1 1 auto',
                overflow: 'auto',
                minHeight: 0,
                padding: '0 28px 28px',
              }}
            >
              <CreditTransactionsTable
                transactions={creditTransactions}
                loading={creditLoading}
                emptyLabel={translate(
                  'pages.admin.admin_users_page.admin_users_page.credit_transactions_table.empty_label',
                )}
              />

              {!creditLoading && creditTransactions.length > 0 && (
                <InfiniteScroll
                  hasMore={creditPage < creditTotalPages}
                  isLoading={creditLoadingMore}
                  onLoadMore={() =>
                    void loadCreditTransactions(modal.user.account.id, creditPage + 1, 'append')
                  }
                  rootMargin="160px"
                />
              )}
            </div>
          </div>
        </Overlay>
      )}

      {modal.type === 'membership' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <form
            className="af-card"
            style={{ width: '100%', maxWidth: 540, padding: 28, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}
            onSubmit={(event) => void grantManualMembership(event)}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_users_page.admin_users_page.h2.membership_active')}
            </h2>
            <div
              className="af-mono"
              style={{ fontSize: 12, color: 'var(--af-muted)', marginBottom: 18 }}
            >
              {modal.user.account.email}
            </div>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              {translate('pages.admin.admin_users_page.admin_users_page.p.payment_membership_user')}
            </p>
            {membershipLoading ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--af-muted)' }}
              >
                <Spinner />{' '}
                {translate('pages.admin.admin_users_page.admin_users_page.div.loading_membership')}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_users_page.admin_users_page.label_label.tenant_2')}

                  <CustomSelect
                    value={membershipForm.tenantId}
                    onChange={(tenantId) =>
                      setMembershipForm((current) => ({ ...current, tenantId }))
                    }
                    options={membershipTenants.map((tenant) => ({
                      value: tenant.id,
                      label: translate(
                        'pages.admin.admin_users_page.admin_users_page.custom_select.options_6',
                        {
                          value1: tenant.name,
                          value2: tenant.slug,
                        },
                      ),
                    }))}
                    placeholder={translate(
                      'pages.admin.admin_users_page.admin_users_page.custom_select.placeholder_tenant_select_2',
                    )}
                  />
                </label>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_users_page.admin_users_page.label_label.membership',
                  )}

                  <CustomSelect
                    value={membershipForm.tier}
                    onChange={(tier) =>
                      setMembershipForm((current) => ({ ...current, tier: tier as MembershipTier }))
                    }
                    options={membershipPlans.map((plan) => ({
                      value: plan.tier,
                      label: translate(
                        'pages.admin.admin_users_page.admin_users_page.custom_select.options_7',
                        {
                          value1: plan.name,
                          value2: plan.tier,
                        },
                      ),
                    }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_users_page.admin_users_page.label_label.time')}

                  <input
                    className="af-input"
                    type="number"
                    min="1"
                    max="60"
                    step="1"
                    value={membershipForm.months}
                    onChange={(event) =>
                      setMembershipForm((current) => ({ ...current, months: event.target.value }))
                    }
                    required
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_memberships_page.admin_memberships_page.label_label.credit_edit')}
                  <input
                    className="af-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={membershipForm.creditAmount}
                    placeholder={String((membershipPlans.find((plan) => plan.tier === membershipForm.tier)?.creditAmountInSmallestUnit ?? 0) / 100)}
                    onChange={(event) => setMembershipForm((current) => ({ ...current, creditAmount: event.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_memberships_page.admin_memberships_page.label_label.slot')}
                  <input
                    className="af-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={membershipForm.slotPrice}
                    placeholder={String((membershipPlans.find((plan) => plan.tier === membershipForm.tier)?.slotPriceInSmallestUnit ?? 0) / 100)}
                    onChange={(event) => setMembershipForm((current) => ({ ...current, slotPrice: event.target.value }))}
                  />
                </label>
              </div>
            )}
            {error && (
              <div className="af-error" style={{ marginTop: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_users_page.admin_users_page.button_outline_btn.cancel_2',
                )}
              </button>
              <button
                className="af-primary-btn"
                style={{ padding: '10px 20px', fontSize: 14 }}
                disabled={
                  saving ||
                  membershipLoading ||
                  !membershipForm.tenantId ||
                  membershipPlans.length === 0
                }
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate(
                    'pages.admin.admin_users_page.admin_users_page.button_primary_btn.payment_active',
                  )
                )}
              </button>
            </div>
          </form>
        </Overlay>
      )}
    </AdminLayout>
  );
}

interface UserAction {
  label: string;
  tone?: 'danger' | 'success';
  disabled?: boolean;
  onSelect: () => void;
}

function ColumnVisibilityMenu({
  columns,
  visibleColumns,
  onToggle,
  onShowAll,
}: {
  columns: UserColumnDefinition[];
  visibleColumns: Set<UserColumnId>;
  onToggle: (columnId: UserColumnId) => void;
  onShowAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    function closeOnOutsideClick(event: PointerEvent) {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div className="af-column-picker" ref={rootRef}>
      <button
        className="af-outline-btn"
        style={{ padding: '12px 16px' }}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {translate('pages.admin.admin_users_page.column_visibility_menu.button_outline_btn.label')}
      </button>
      {open && (
        <div className="af-column-picker-menu" role="menu">
          <div className="af-column-picker-header">
            <span>
              {translate('pages.admin.admin_users_page.column_visibility_menu.span.copy')}
            </span>
            {visibleColumns.size < columns.length && (
              <button type="button" onClick={onShowAll}>
                {translate('pages.admin.admin_users_page.column_visibility_menu.button.label')}
              </button>
            )}
          </div>
          <div className="af-column-picker-options">
            {columns.map((column) => (
              <label key={column.id} className="af-column-picker-option">
                <input
                  type="checkbox"
                  checked={visibleColumns.has(column.id)}
                  disabled={visibleColumns.size === 1 && visibleColumns.has(column.id)}
                  onChange={() => onToggle(column.id)}
                />

                <span>{column.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UserActionsDropdown({ actions, disabled }: { actions: UserAction[]; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updateMenuPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const viewportPadding = 8;
    const gap = 6;
    const menuRect = menuListRef.current?.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? actions.length * 36 + 12;
    const menuWidth = menuRect?.width ?? 150;
    const topBelow = rect.bottom + gap;
    const topAbove = rect.top - gap - menuHeight;
    const fitsBelow = topBelow + menuHeight <= window.innerHeight - viewportPadding;
    const top = fitsBelow
      ? topBelow
      : Math.max(
          viewportPadding,
          Math.min(topAbove, window.innerHeight - menuHeight - viewportPadding),
        );

    setMenuPosition({
      top,
      left: Math.max(
        viewportPadding,
        Math.min(window.innerWidth - menuWidth - viewportPadding, rect.right - menuWidth),
      ),
    });
  }, [actions.length]);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();

    function closeOnOutsideClick(event: MouseEvent) {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target) &&
        !menuListRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  function selectAction(action: UserAction) {
    setOpen(false);
    action.onSelect();
  }

  if (actions.length === 0) {
    return <span style={{ color: 'var(--af-dim)' }}>{'-'}</span>;
  }

  return (
    <div className={`af-actions-menu${open ? ' is-open' : ''}`} ref={menuRef}>
      <button
        ref={triggerRef}
        className="af-actions-menu-trigger"
        type="button"
        aria-label={translate(
          'pages.admin.admin_users_page.user_actions_dropdown.button_actions_menu_trigger.aria_label_user',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <span aria-hidden="true">{'...'}</span>
      </button>
      {open &&
        createPortal(
          <div
            className="af-actions-menu-list"
            ref={menuListRef}
            role="menu"
            style={menuPosition ?? undefined}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                className={`af-actions-menu-item${
                  action.tone ? ` af-actions-menu-item--${action.tone}` : ''
                }`}
                type="button"
                role="menuitem"
                disabled={action.disabled || disabled}
                onClick={() => selectAction(action)}
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function accountLabel(account?: AccountCreditTransaction['account'] | null) {
  if (!account) return '-';
  return [account.firstName, account.lastName].filter(Boolean).join(' ') || account.email;
}

function CreditTransactionsTable({
  transactions,
  loading,
  emptyLabel,
}: {
  transactions: AccountCreditTransaction[];
  loading: boolean;
  emptyLabel: string;
}) {
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 18,
          color: 'var(--af-muted)',
        }}
      >
        <Spinner />{' '}
        {translate(
          'pages.admin.admin_users_page.credit_transactions_table.div.loading_credit_edit',
        )}
      </div>
    );
  }

  if (transactions.length === 0) {
    return <div style={{ padding: 18, color: 'var(--af-muted)', fontSize: 13 }}>{emptyLabel}</div>;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--af-card-bg)',
        }}
      >
        <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
          {[
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.copy'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.scope'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.type'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.copy_2'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.reason'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.date'),
            translate('pages.admin.admin_users_page.credit_transactions_table.tr.copy_3'),
          ].map((heading) => (
            <th
              key={heading}
              style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 10,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--af-muted)',
              }}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transactions.map((transaction, index) => (
          <tr
            key={transaction.id}
            style={{
              borderBottom: index < transactions.length - 1 ? '1px solid var(--af-border)' : 'none',
            }}
          >
            <td style={{ padding: '12px 16px' }}>{accountLabel(transaction.account)}</td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {transaction.tenantId
                ? transaction.tenantId.slice(0, 8)
                : translate('pages.admin.admin_users_page.credit_transactions_table.td.copy')}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {creditTypeLabels[transaction.type]}
            </td>
            <td
              style={{
                padding: '12px 16px',
                fontWeight: 900,
                color:
                  transaction.amountInSmallestUnit >= 0 ? 'var(--af-success)' : 'var(--af-danger)',
              }}
            >
              {transaction.amountInSmallestUnit >= 0 ? '+' : ''}
              {formatCurrency(transaction.amountInSmallestUnit, transaction.currency)}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)', maxWidth: 260 }}>
              {transaction.reason}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {formatDate(transaction.createdAt)}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {accountLabel(transaction.performedByAccount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
